import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { SHOW_COUNT } from '@/store/models/collectionItems/CollectionItem'
import eventbus from '@/plugins/eventbus'
import errorHandler from '@/plugins/error-handler'
import importProgressWS from '@/services/websocket/import-progress'
import { useItemStoreState } from '@/composables/useItemStoreState'
import { useUrlFilterSync } from '@/composables/useUrlFilterSync'
import { createQueuedTask } from '@/utils/helpers'
import {
  clearCollectionItemsCache,
  computeFiltersActive,
  queryPinnedItems,
  queryRegularItems,
  createGuardedLoader,
  createFilterWatcher,
  createItemsLoader,
  createScrollHandler,
} from '@/utils/item-view-utils'

/* global CONFIG */

/**
 * Composable for collection item view functionality (All.vue, Deleted.vue).
 * Uses the 'filters' / 'defaultFilterFn' store getters which include isFavorite.
 */
export const useCollectionItemView = () => {
  const route = useRoute()

  // ==================== SHARED STORE STATE ====================
  const {
    store, userId, collectionId, listStyle, isExtensionPopup, isDesktop, isMobile,
    itemShow, itemNext, itemFilters, itemDefaultFilterFn,
    sorting, collectionListStyle, itemCount, filteredItemCount, isFilterSidebarOpen,
    importQueueSize, sumImportQueueSize, lastImportSize, activeImport, hasActiveFilters,
    setCollectionId, setListStyle, setItemShow, setItemNext,
    setCollectionListStyle, setFilterSidebarOpen, setFilteredItemCount,
    setItemFilters, setFilterTags, reset,
    getPinnedItems, getItemStats, getPopularTags,
    onOpenFilter, resetFilters, setSorting, searchValue,
  } = useItemStoreState({ filterGetter: 'filters', defaultFilterGetter: 'defaultFilterFn' })

  // ==================== URL FILTER SYNC ====================
  const { setFiltersFromUrl, setUrlFromFilters } = useUrlFilterSync({
    filters: itemFilters,
    defaultFilterFn: itemDefaultFilterFn,
    setFilters: setItemFilters,
    parseOptions: { coerceIsFavorite: true, validateResourceType: true },
  })

  // ==================== LOCAL STATE ====================
  // TODO: This is wrong. This comes from badly written mixin from vue 2 implementation.
  // Deleted items should be placed in a different composable function
  const handleDeletedItems = ref(false)
  const loading = ref({ pinnedItems: false, items: false, stats: false, tags: false })
  const refreshInterval = ref(null)
  const pinnedFilteredCount = ref(0)
  const useWebSocketProgress = ref(CONFIG.websocket?.enabled !== false)
  const wsConnectionFailed = ref(false)

  // ==================== COMPUTED ====================
  const pinnedItems = computed(() => {
    if (handleDeletedItems.value) return []
    return queryPinnedItems(collectionId.value, itemFilters.value, { applyItemFilters: true })
  })

  const regularItems = computed(() =>
    queryRegularItems(collectionId.value, itemFilters.value, itemShow.value, { applyItemFilters: true, skipPinFilter: handleDeletedItems.value }),
  )

  const items = computed(() => [...pinnedItems.value, ...regularItems.value])

  const filtersActive = computed(() =>
    computeFiltersActive(itemFilters.value, itemDefaultFilterFn.value(), { checkFavorite: true, checkResourceType: true }),
  )

  // ==================== STATS (guarded loaders) ====================
  const onGetStats = createGuardedLoader(loading, 'stats', getItemStats)
  const onGetPopularTags = createGuardedLoader(loading, 'tags', getPopularTags)

  // ==================== LOADING ====================
  const loadPinnedItems = async (forced = false) => {
    if (handleDeletedItems.value) return
    if (loading.value.pinnedItems && !forced) return
    loading.value.pinnedItems = true
    try {
      const { itemCount: pinnedCount } = await getPinnedItems()
      pinnedFilteredCount.value = pinnedCount || 0
    } catch (error) {
      errorHandler.toast(error)
    } finally {
      loading.value.pinnedItems = false
    }
  }

  const loadItems = createItemsLoader({
    loading,
    getAction: () => store.dispatch(handleDeletedItems.value ? 'collectionItems/getDeleted' : 'collectionItems/getItems'),
    setNext: setItemNext,
    setFilteredItemCount,
    setShow: setItemShow,
    getShow: () => itemShow.value,
    pinnedFilteredCount,
  })

  const clearCollectionItems = (targetCollectionId = collectionId.value) =>
    clearCollectionItemsCache(targetCollectionId)

  const normalizeReloadOptions = (options = {}) => ({
    includeStats: false,
    syncUrl: false,
    forceItems: true,
    ...options,
  })

  const mergeReloadOptions = (current, next) => {
    const currentOptions = normalizeReloadOptions(current)
    const nextOptions = normalizeReloadOptions(next)

    return {
      includeStats: currentOptions.includeStats || nextOptions.includeStats,
      syncUrl: currentOptions.syncUrl || nextOptions.syncUrl,
      forceItems: currentOptions.forceItems || nextOptions.forceItems,
    }
  }

  // Centralize full list reloads so refresh paths stay aligned.
  const runCollectionItemsReload = async ({
    includeStats = false,
    syncUrl = false,
    forceItems = true,
  } = {}) => {
    loading.value.pinnedItems = true
    try {
      await clearCollectionItems()
      resetPagination()

      if (includeStats) {
        await Promise.all([onGetStats(), onGetPopularTags()])
      }

      await loadPinnedItems(true)
      await loadItems(forceItems)

      if (syncUrl) {
        setUrlFromFilters()
      }
    } finally {
      if (!loading.value.items) {
        loading.value.pinnedItems = false
      }
    }
  }

  const reloadCollectionItems = createQueuedTask(runCollectionItemsReload, mergeReloadOptions)

  const refreshItems = async () => {
    try {
      await reloadCollectionItems({ includeStats: true })
    } catch (error) {
      console.error(error)
    }
  }

  // ==================== PAGINATION ====================
  const resetPagination = () => {
    pinnedFilteredCount.value = 0
    setItemNext(null)
    setItemShow(SHOW_COUNT)
  }

  // ==================== PULL TO REFRESH ====================
  const ptrOnRefresh = () => reloadCollectionItems()

  // ==================== INFINITE SCROLL ====================
  const handleScroll = createScrollHandler({
    isLoading: () => loading.value.pinnedItems || loading.value.items,
    hasMore: () => !!itemNext.value,
    loadMore: loadItems,
  })

  // ==================== WATCHERS ====================
  const reloadAll = () => reloadCollectionItems({ syncUrl: true })

  createFilterWatcher({
    filters: itemFilters,
    onDebouncedSearch: reloadAll,
    onImmediateChange: reloadAll,
    minSearchLength: 0,
  })

  watch(() => store.getters['auth/collectionId'], (newAuthCollectionId, oldAuthCollectionId) => {
    if (!oldAuthCollectionId && newAuthCollectionId && !route.params.id && !store.getters['collectionItems/currentRouteCollectionId']) {
      console.log('[CollectionItemView] Auth collectionId now available, refreshing items')
      setCollectionId(null)
      refreshItems()
    }
  })

  // ==================== WEBSOCKET / IMPORT ====================
  const handleImportCompleted = ({ importId, status }) => {
    console.log('[CollectionItemView] Import completed (for refresh):', importId, status)

    const storedImport = activeImport.value
    if (storedImport?.importId === importId) {
      store.commit('collectionItems/clearActiveImport')
    }

    const autoRefreshEnabled = CONFIG?.features?.autoRefreshAfterImport ?? false

    if (status === 'completed' && autoRefreshEnabled) {
      console.log('[CollectionItemView] Auto-refreshing items (feature enabled)')
      refreshItems()
    } else if (status === 'completed') {
      console.log('[CollectionItemView] Import completed but auto-refresh disabled - user must click "Load items"')
    }
  }

  const handleManualRefresh = () => {
    console.log('[CollectionItemView] Manual refresh requested - loading imported items')
    refreshItems()
  }

  const handleWebSocketConnectionFailed = () => {
    console.warn('[CollectionItemView] WebSocket connection failed')
    wsConnectionFailed.value = true
    useWebSocketProgress.value = false
  }

  const handleWebSocketMaxReconnectFailed = () => {
    console.error('[CollectionItemView] WebSocket max reconnect attempts reached')
    handleWebSocketConnectionFailed()
  }

  const setupWebSocketListeners = () => {
    eventbus.on('import:completed', handleImportCompleted)
    eventbus.on('import:ws:connection-failed', handleWebSocketConnectionFailed)
    eventbus.on('import:ws:max-reconnect-failed', handleWebSocketMaxReconnectFailed)
    eventbus.on('collectionItems:manual-refresh', handleManualRefresh)
  }

  // ==================== LIFECYCLE ====================
  const initializeCollectionItemView = async () => {
    const routeCollectionId = route.params.id ?? null
    setCollectionId(routeCollectionId)
    setFiltersFromUrl()
    setupWebSocketListeners()

    try {
      await reloadCollectionItems({ includeStats: true, forceItems: false })
    } catch (error) {
      console.error(error)
    }
  }

  const cleanupCollectionItemView = () => {
    setCollectionId(null)

    eventbus.off('import:completed', handleImportCompleted)
    eventbus.off('import:ws:connection-failed', handleWebSocketConnectionFailed)
    eventbus.off('import:ws:max-reconnect-failed', handleWebSocketMaxReconnectFailed)
    eventbus.off('collectionItems:manual-refresh', handleManualRefresh)

    const storedImport = activeImport.value
    if (storedImport?.importId) {
      importProgressWS.stopTracking(storedImport.importId)
    }

    if (refreshInterval.value) {
      clearInterval(refreshInterval.value)
      refreshInterval.value = null
    }
  }

  // ==================== ADDITIONAL SETTERS ====================
  const setResourceType = resourceType => store.dispatch('collectionItems/setResourceType', resourceType)

  return {
    // State
    loading,
    handleDeletedItems,
    activeImport,
    useWebSocketProgress,

    // Computed properties
    userId,
    listStyle,
    isExtensionPopup,
    isDesktop,
    isMobile,
    itemShow,
    itemNext,
    itemFilters,
    itemDefaultFilterFn,
    sorting,
    collectionListStyle,
    itemCount,
    importQueueSize,
    sumImportQueueSize,
    lastImportSize,
    collectionId,
    filteredItemCount,
    hasActiveFilters,
    items,
    filtersActive,
    searchValue,
    isFilterSidebarOpen,

    // Mutations
    setCollectionId,
    setListStyle,
    reset,
    setItemShow,
    setItemNext,
    setCollectionListStyle,
    setFilterSidebarOpen,
    setFilteredItemCount,
    setSorting,
    setFilterTags,
    setResourceType,

    // Methods
    refreshItems,
    loadItems,
    handleScroll,
    onGetStats,
    onGetPopularTags,
    setFiltersFromUrl,
    setUrlFromFilters,
    onOpenFilter,
    resetFilters,
    resetPagination,
    ptrOnRefresh,

    // Lifecycle
    initializeCollectionItemView,
    cleanupCollectionItemView,
  }
}
